import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ref, getDownloadURL } from 'firebase/storage'
import { removeJob } from '../lib/api.js'
import { storage } from '../lib/firebase.js'
import { usePlayer } from '../hooks/usePlayer.js'

interface DubCardProps {
  job: {
    id: string
    status: string
    videoTitle?: string
    videoUrl?: string
    targetLang: string
    sourceLang?: string
    videoThumbnailUrl?: string
    durationSeconds?: number
    outputUrl?: string | null
    outputStoragePath?: string | null
    originalStoragePath?: string | null
  }
}

const THUMB_VARIANTS = ['v1', 'v2', 'v3', 'v4', 'v5', 'v6']

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export default function DubCard({ job }: DubCardProps) {
  const { t } = useTranslation()
  const player = usePlayer()
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState(false)

  const variant = THUMB_VARIANTS[parseInt(job.id.slice(-2), 16) % 6]
  const title = job.videoTitle || t('job.untitled')
  const langName = t(`languages.${job.targetLang}`, job.targetLang.toUpperCase())
  const dur = job.durationSeconds ? formatDuration(job.durationSeconds) : ''
  const hasRealThumb = job.videoThumbnailUrl?.startsWith('http')
  const isDone = job.status === 'ready'
  const isActive = !['ready', 'failed', 'expired', 'queued'].includes(job.status)
  const statusClass = job.status === 'failed' ? 'failed'
    : job.status === 'queued' ? 'queued'
    : isActive ? 'active'
    : 'done'

  // Resolve audio URL for ready jobs
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!isDone) return
    const path = job.outputUrl ?? job.outputStoragePath ?? null
    if (!path) return
    if (path.startsWith('http')) { setAudioUrl(path); return }
    getDownloadURL(ref(storage, path))
      .then(url => setAudioUrl(url))
      .catch(() => {})
  }, [isDone, job.outputUrl, job.outputStoragePath])

  const deleteMut = useMutation({
    mutationFn: () => removeJob(job.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] })
      setConfirming(false)
    },
  })

  const handleClick = useCallback(() => {
    if (confirming) return
    if (!isDone || !audioUrl) return
    player.openMiniPlayer(job.id, audioUrl, {
      videoTitle: job.videoTitle ?? t('job.untitled'),
      videoUrl: job.videoUrl ?? '',
      videoThumbnailUrl: job.videoThumbnailUrl ?? '',
      targetLang: job.targetLang,
      sourceLang: job.sourceLang ?? 'auto',
      durationSeconds: job.durationSeconds ?? 0,
      originalStoragePath: job.originalStoragePath ?? null,
    })
    player.openFullScreen()
  }, [confirming, isDone, audioUrl, job, player])

  const handleTrash = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setConfirming(true)
  }, [])

  const handleConfirmDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    deleteMut.mutate()
  }, [deleteMut])

  const handleCancelDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setConfirming(false)
  }, [])

  return (
    <div className="dub-card" onClick={handleClick} style={{ cursor: isDone && audioUrl ? 'pointer' : 'default' }}>
      <div className="dub-card-thumb">
        {hasRealThumb ? (
          <img src={job.videoThumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div className={`dub-thumb-bg ${variant}`} />
        )}
        {isDone && audioUrl && (
          <div className="dub-play-btn">
            <svg viewBox="0 0 24 24"><polygon points="8 5 19 12 8 19" /></svg>
          </div>
        )}
        <div className="dub-lang-badge">{langName}</div>

        {/* Delete button — visible on hover */}
        <button className="dub-delete-btn" onClick={handleTrash} title={t('common.delete', 'Delete')}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
            <path d="M10 11v6" /><path d="M14 11v6" />
          </svg>
        </button>
      </div>

      {/* Confirmation overlay */}
      {confirming && (
        <div className="dub-confirm-overlay" onClick={e => e.stopPropagation()}>
          <p>{t('dashboard.deleteConfirm', 'Delete this dub?')}</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="dub-confirm-cancel" onClick={handleCancelDelete}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button className="dub-confirm-delete" onClick={handleConfirmDelete} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? '...' : t('common.delete', 'Delete')}
            </button>
          </div>
        </div>
      )}

      <div className="dub-card-info">
        <div className="dub-card-title">{title}</div>
        <div className="dub-card-meta">
          {job.status !== 'ready' && (
            <span className={`dub-status-badge ${statusClass}`}>
              {isActive && <span className="dub-status-dot" />}
              {t(`job.status.${job.status}`)}
            </span>
          )}
          {job.status === 'ready' && (
            <span className="dub-status-badge done">
              {'✓ '}{t('job.status.ready')}
            </span>
          )}
        </div>
      </div>
      <div className="dub-card-footer">
        <span className="dub-card-dur">{dur}</span>
        <span className="dub-card-pill">{langName}</span>
      </div>
    </div>
  )
}
